import axiosInstance from './axiosInstance';

export interface OrderTrackingData {
  order: {
    _id: string;
    orderNumber: string;
    customerName: string;
    customerPhone: string;
    pickupStatus: string;
    priority?: string;
    createdAt: string;
    pharmacyId?: {
      _id: string;
      pharmacyName: string;
      location?: {
        latitude: number;
        longitude: number;
        address: string;
      };
    };
    riderInfo?: {
      id: string;
      name: string;
      phone: string;
      vehicleType: string;
      vehicleNumber: string;
      availability: string;
    };
    location?: {
      latitude: number;
      longitude: number;
      address: string;
    };
  };
  trackingData?: {
    latitude: number;
    longitude: number;
    speed: number;
    heading: number;
    lastUpdated: string;
    isOnline: boolean;
  };
  routeHistory: Array<{
    latitude: number;
    longitude: number;
    timestamp: string;
    speed: number;
    heading: number;
  }>;
  estimatedDelivery?: string;
  deliveryLocation?: {
    latitude: number;
    longitude: number;
    address: string;
  };
  pharmacyLocation?: {
    latitude: number;
    longitude: number;
    address: string;
  };
  routeData?: {
    _id: string;
    routeId: string;
    name: string;
    description: string;
    waypoints: Array<{
      _id?: string;
      lat: number;
      lng: number;
      address: string;
      order: number;
      type: 'pharmacy' | 'delivery' | 'other';
      pharmacy?: string;
      routeType?: string;
      completed?: boolean;
    }>;
    assignedDriver: string;
    estimatedTime: number;
    distance: number;
    status: string;
    routeType: string;
    orders: string[];
    pickupOrder: string[];
    completedWaypoints: string[];
  };
}

export interface TrackingOrder {
  _id: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  pickupStatus: string;
  deliveryStatus?: string;
  priority?: string;
  createdAt: string;
  pharmacyId: {
    _id: string;
    pharmacyName: string;
  };
  riderInfo?: {
    id: string;
    name: string;
    phone: string;
    vehicleType: string;
    vehicleNumber: string;
    availability: string;
  };
  trackingData?: {
    latitude: number;
    longitude: number;
    speed: number;
    heading: number;
    lastUpdated: string;
    isOnline: boolean;
  };
  isRiderOnRoute?: boolean;
}

export interface TrackingResponse {
  success: boolean;
  message: string;
  data: {
    orders: TrackingOrder[];
    pagination: {
      current: number;
      total: number;
      count: number;
      totalCount: number;
    };
  };
}

export interface RouteTrackingResponse {
  success: boolean;
  message: string;
  data: {
    routes: any[];
    pagination: {
      current: number;
      total: number;
      count: number;
      totalCount: number;
    };
  };
}

export interface OrderTrackingResponse {
  success: boolean;
  message: string;
  data: OrderTrackingData;
}

export interface RiderLocationResponse {
  success: boolean;
  message: string;
  data: {
    latitude: number;
    longitude: number;
    speed: number;
    heading: number;
    timestamp: string;
    isOnline: boolean;
  };
}

// Admin tracking service
export const adminTrackingService = {
  // Get all orders with tracking data for admin
  getAllOrdersWithTracking: async (filters: {
    status?: string;
    priority?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<TrackingResponse> => {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await axiosInstance.get(`/admin/orders/tracking?${params}`);
    return response.data;
  },

  // Get all active routes with complete tracking data for admin
  getAllActiveRoutesWithTracking: async (filters: {
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<RouteTrackingResponse> => {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await axiosInstance.get(`/admin/routes/tracking?${params}`);
    return response.data;
  }
};

// Pharmacy tracking service
export const pharmacyTrackingService = {
  // Get pharmacy orders with tracking data
  getPharmacyOrdersWithTracking: async (filters: {
    status?: string;
    priority?: string;
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<TrackingResponse> => {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await axiosInstance.get(`/pharmacy/orders/tracking?${params}`);
    return response.data;
  },

  // Get pharmacy routes with tracking data
  getPharmacyRoutesWithTracking: async (filters: {
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<RouteTrackingResponse> => {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await axiosInstance.get(`/pharmacy/routes/tracking?${params}`);
    return response.data;
  }
};

// General tracking service
export const trackingService = {
  // Get detailed tracking information for a specific order
  getOrderTrackingDetails: async (orderId: string): Promise<OrderTrackingResponse> => {
    const response = await axiosInstance.get(`/orders/${orderId}/live-tracking`);
    return response.data;
  },

  // Get rider's live location for real-time updates
  getRiderLiveLocation: async (riderId: string): Promise<RiderLocationResponse> => {
    const response = await axiosInstance.get(`/riders/${riderId}/live-location`);
    return response.data;
  },

  // Get route data for tracking
  getRouteData: async (routeId: string): Promise<any> => {
    const response = await axiosInstance.get(`/routes/${routeId}`);
    return response.data;
  }
};

export default {
  admin: adminTrackingService,
  pharmacy: pharmacyTrackingService,
  tracking: trackingService
};
